// src/pages/team/TeamPage.tsx - Updated with real API data
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PlusIcon,
  MinusIcon,
  TrophyIcon,
  ChartBarIcon,
  UserPlusIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { Player, RosterSlot } from '../../types';
import { useApi } from '../../hooks/useApi';
import { seasonAverage } from '../../utils/playerSeason';
import apiService from '../../services/api';
import AddPlayerModal from '../../components/team/AddPlayerModal';
import DropPlayerModal from '../../components/team/DropPlayerModal';
import LineChart from '../../components/charts/LineChart';
import StackedBars from '../../components/charts/StackedBars';
import { teamWeeklySeries, leagueMedianSeries } from '../../utils/chartData';

export default function TeamPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<'lineup' | 'roster' | 'stats'>('lineup');
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showDropPlayer, setShowDropPlayer] = useState(false);
  const [playerToDrop, setPlayerToDrop] = useState<Player | null>(null);
  const [lineupError, setLineupError] = useState<string | null>(null);
  const [togglingPlayerId, setTogglingPlayerId] = useState<number | null>(null);
  const [waiversEnabled, setWaiversEnabled] = useState(false);

  // Fetch team data from real API
  const { data: team, loading: teamLoading, error: teamError } = useApi(
    () => teamId ? apiService.getTeam(parseInt(teamId)) : Promise.reject('No team ID'),
    [teamId]
  );

  // Fetch roster data from real API
  const { data: rosterSlots, loading: rosterLoading, refetch: refetchRoster } = useApi(
    () => teamId ? apiService.getRoster(parseInt(teamId)) : Promise.resolve([]),
    [teamId]
  );

  // Fetch the league's waivers_enabled flag so Add Player can route accordingly
  useEffect(() => {
    const leagueIdForWaivers = team?.league_id || team?.leagueId;
    if (!leagueIdForWaivers) return;
    apiService.getLeague(leagueIdForWaivers)
      .then((lg) => setWaiversEnabled(lg.waivers_enabled))
      .catch(() => {});
  }, [team?.league_id, team?.leagueId]);

  // Fetch matchups (weekly trend chart) and K/P weekly splits (stacked bars)
  const chartLeagueId = team?.league_id || team?.leagueId;
  const { data: teamMatchups, loading: matchupsLoading, error: matchupsError } = useApi(
    () => chartLeagueId ? apiService.getMatchups(chartLeagueId) : Promise.resolve([]),
    [chartLeagueId]
  );
  const { data: weeklySplits } = useApi(
    () => teamId ? apiService.getTeamWeeklySplits(parseInt(teamId)).catch(() => []) : Promise.resolve([]),
    [teamId]
  );

  // Derive lineup sections directly from rosterSlots — no mapping needed
  const starterKickers = rosterSlots?.filter(s => s.player?.position === 'K' && s.is_starter) ?? [];
  const starterPunters = rosterSlots?.filter(s => s.player?.position === 'P' && s.is_starter) ?? [];
  const bench          = rosterSlots?.filter(s => !s.is_starter) ?? [];

  const handleToggleStarter = async (slot: RosterSlot, isStarter: boolean) => {
    if (!teamId) return;
    setTogglingPlayerId(slot.player_id);
    setLineupError(null);
    try {
      await apiService.updateStarterStatus(parseInt(teamId), slot.player_id, isStarter);
      await refetchRoster();
    } catch (error: any) {
      setLineupError(error.message || 'Failed to update lineup');
    } finally {
      setTogglingPlayerId(null);
    }
  };

  const handleSwapPlayer = async (playerAddId: number, playerDropId?: number) => {
    if (!teamId) return;
    await apiService.swapPlayer(parseInt(teamId), playerAddId, playerDropId);
    await refetchRoster();
  };

  const handleDropPlayer = (player: Player) => {
    setPlayerToDrop(player);
    setShowDropPlayer(true);
  };

  const confirmDropPlayer = async () => {
    if (!playerToDrop || !teamId) return;
    try {
      await apiService.removePlayerFromRoster(parseInt(teamId), playerToDrop.id);
      await refetchRoster();
      setPlayerToDrop(null);
    } catch (error: any) {
      alert(error.message || 'Failed to drop player');
    }
  };

  // Starters' per-game averages added up: what this lineup has been worth in an
  // average week. It was labelled "Projected" over a sum of
  // players.fantasy_points_avg, which made it wrong twice — a stale column, and
  // a projection it never was. Same arithmetic, this season's numbers, and a
  // label that says which season they belong to.
  const starterSlots = [...starterKickers, ...starterPunters];

  const getStarterAvgTotal = () =>
    starterSlots.reduce((sum, s) => sum + seasonAverage(s.player).avg, 0);

  const starterAvgSeason = starterSlots
    .map(s => s.player?.display_season)
    .find(season => season != null);

  const getPositionColor = (position: string) =>
    position === 'K' ? 'bg-secondary-900 text-gold' : 'bg-success-100 text-success-800';

  const isPlayerStarter = (playerId: number) =>
    rosterSlots?.some(s => s.is_starter && s.player?.id === playerId) ?? false;

  const getAllRosterPlayers = (): Player[] =>
    rosterSlots?.map(s => s.player).filter(Boolean) as Player[] ?? [];

  if (teamLoading || rosterLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Link to="/app/leagues" className="btn-outline-secondary btn-sm">
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to League
          </Link>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-center py-12">
              <div className="loading-spinner w-8 h-8 mr-3"></div>
              <span>Loading team data...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (teamError || !team) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Link to="/app/leagues" className="btn-outline-secondary btn-sm">
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to League
          </Link>
        </div>
        <div className="card">
          <div className="card-body text-center py-12">
            <h3 className="text-lg font-semibold text-secondary-900 mb-2">
              {teamError ? 'Error Loading Team' : 'Team Not Found'}
            </h3>
            <p className="text-secondary-600 mb-4">
              {teamError || 'The team you\'re looking for doesn\'t exist.'}
            </p>
            <div className="flex justify-center space-x-3">
              <Link to="/app/leagues" className="btn-primary">
                Back to Leagues
              </Link>
              <a href="/api-test" className="btn-outline-secondary">
                Test API Connection
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle both field formats
  const teamName = team.team_name || team.teamName || 'Unknown Team';
  const leagueId = team.league_id || team.leagueId;
  const pointsFor = team.points_for || team.pointsFor || 0;
  const wins = team.wins || 0;
  const losses = team.losses || 0;

  const myTrend = teamMatchups && team ? teamWeeklySeries(teamMatchups, team.id) : [];
  const medianSeries = teamMatchups ? leagueMedianSeries(teamMatchups) : [];
  const roundedMedianSeries = medianSeries.map(p => ({ x: p.x, y: Math.round(p.y * 10) / 10 }));
  const weeks = myTrend.map(p => p.x);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to={`/app/leagues/${leagueId}`}
          className="inline-flex items-center text-sm font-semibold uppercase tracking-wide text-secondary-600 hover:text-ink-900 mb-3"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-1.5" />
          Back to League
        </Link>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">{teamName}</h1>
            <div className="flex items-center gap-x-4 mt-1">
              <span className="text-sm text-secondary-600">
                {wins}-{losses} Record
              </span>
              <span className="text-sm text-secondary-600">
                {Number(pointsFor).toFixed(1)} Points For
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => waiversEnabled ? navigate(`/app/leagues/${leagueId}/waivers`) : setShowAddPlayer(true)}
              className="btn-primary btn-sm"
            >
              <UserPlusIcon className="w-4 h-4 mr-2" />
              Add Player
            </button>
            <button className="btn-outline-secondary btn-sm">
              <Cog6ToothIcon className="w-4 h-4 mr-2" />
              Settings
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-secondary-200">
        <nav className="-mb-px flex space-x-8">
          {(['lineup', 'roster', 'stats'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-3 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Lineup Tab */}
      {activeTab === 'lineup' && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">

            {lineupError && (
              <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg text-sm">
                {lineupError}
              </div>
            )}

            {/* Starting Kickers */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <h3 className="font-display uppercase text-lg tracking-wide text-ink-900">Starting Kickers</h3>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-secondary-500">{starterKickers.length} / 2</span>
                    <span className="text-sm text-secondary-600">
                      {starterAvgSeason ? `${starterAvgSeason} Avg/Wk` : 'Avg/Wk'}:{' '}
                      <span className="font-medium text-primary-600" data-testid="starter-avg-total">
                        {getStarterAvgTotal().toFixed(1)} pts
                      </span>
                    </span>
                  </div>
                </div>
              </div>
              <div className="card-body space-y-3">
                {starterKickers.map(slot => (
                  <div key={slot.id} className="lineup-slot lineup-slot-filled">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPositionColor('K')}`}>K</span>
                        <div>
                          <div className="font-medium text-secondary-900">{slot.player?.name}</div>
                          <div className="text-sm text-secondary-500">
                            {slot.player?.nfl_team?.abbreviation || slot.player?.nflTeam?.abbreviation} · {seasonAverage(slot.player).display} pts · {seasonAverage(slot.player).label}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleToggleStarter(slot, false)}
                        disabled={togglingPlayerId === slot.player_id}
                        className="icon-btn text-secondary-400 hover:text-secondary-600 disabled:opacity-40"
                        title="Move to bench"
                      >
                        {togglingPlayerId === slot.player_id
                          ? <div className="loading-spinner w-4 h-4" />
                          : <MinusIcon className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                ))}
                {Array.from({ length: 2 - starterKickers.length }).map((_, i) => (
                  <div key={`empty-k-${i}`} className="lineup-slot">
                    <div className="flex items-center space-x-3">
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-400">K</span>
                      <div className="text-secondary-400 text-sm">Empty slot — add a kicker or promote one from bench</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Starting Punters */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <h3 className="font-display uppercase text-lg tracking-wide text-ink-900">Starting Punters</h3>
                  <span className="text-sm text-secondary-500">{starterPunters.length} / 2</span>
                </div>
              </div>
              <div className="card-body space-y-3">
                {starterPunters.map(slot => (
                  <div key={slot.id} className="lineup-slot lineup-slot-filled">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPositionColor('P')}`}>P</span>
                        <div>
                          <div className="font-medium text-secondary-900">{slot.player?.name}</div>
                          <div className="text-sm text-secondary-500">
                            {slot.player?.nfl_team?.abbreviation || slot.player?.nflTeam?.abbreviation} · {seasonAverage(slot.player).display} pts · {seasonAverage(slot.player).label}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleToggleStarter(slot, false)}
                        disabled={togglingPlayerId === slot.player_id}
                        className="icon-btn text-secondary-400 hover:text-secondary-600 disabled:opacity-40"
                        title="Move to bench"
                      >
                        {togglingPlayerId === slot.player_id
                          ? <div className="loading-spinner w-4 h-4" />
                          : <MinusIcon className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                ))}
                {Array.from({ length: 2 - starterPunters.length }).map((_, i) => (
                  <div key={`empty-p-${i}`} className="lineup-slot">
                    <div className="flex items-center space-x-3">
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-400">P</span>
                      <div className="text-secondary-400 text-sm">Empty slot — add a punter or promote one from bench</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>

          <div>
            <div className="card">
              <div className="card-header">
                <h3 className="font-display uppercase text-lg tracking-wide text-ink-900">Bench</h3>
              </div>
              <div className="card-body space-y-3">
                {bench.length === 0 && (
                  <p className="text-sm text-secondary-400">No bench players</p>
                )}
                {bench.map(slot => (
                  <div key={slot.id} className="lineup-slot">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPositionColor(slot.player?.position ?? '')}`}>
                          {slot.player?.position}
                        </span>
                        <div>
                          <div className="font-medium text-secondary-900 text-sm">{slot.player?.name}</div>
                          <div className="text-xs text-secondary-500">
                            {slot.player?.nfl_team?.abbreviation || slot.player?.nflTeam?.abbreviation} · {seasonAverage(slot.player).display} pts · {seasonAverage(slot.player).label}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleToggleStarter(slot, true)}
                        disabled={togglingPlayerId === slot.player_id}
                        className="icon-btn text-secondary-400 hover:text-success-600 disabled:opacity-40"
                        title="Move to starting lineup"
                      >
                        {togglingPlayerId === slot.player_id
                          ? <div className="loading-spinner w-4 h-4" />
                          : <PlusIcon className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card mt-4">
              <div className="card-header">
                <h3 className="font-display uppercase text-lg tracking-wide text-ink-900">Team Stats</h3>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-2 gap-4">
                  <div className="stat-card">
                    <div className="stat-value">{wins}-{losses}</div>
                    <div className="stat-label">Record</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{Number(pointsFor).toFixed(1)}</div>
                    <div className="stat-label">Points For</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Roster Tab */}
      {activeTab === 'roster' && (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="font-display uppercase text-lg tracking-wide text-ink-900">Full Roster</h3>
              <span className="text-sm text-secondary-600">
                {getAllRosterPlayers().length} / 6 players
              </span>
            </div>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              {getAllRosterPlayers().map((player) => {
                const isStarter = isPlayerStarter(player.id);
                const jerseyNumber = player.jersey_number || player.jerseyNumber;
                const experience = player.experience || player.experienceYears || 0;
                const season = seasonAverage(player);
                
                return (
                  <div key={player.id} className="flex items-center justify-between p-4 border border-secondary-200 rounded-lg hover:bg-secondary-50">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-primary-600">
                          {jerseyNumber}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-secondary-900">{player.name}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPositionColor(player.position)}`}>
                            {player.position}
                          </span>
                          <span className="text-sm text-secondary-600">
                            {player.nfl_team?.abbreviation || player.nflTeam?.abbreviation}
                          </span>
                          {isStarter && (
                            <StarIconSolid className="w-4 h-4 text-warning-500" title="Starting" />
                          )}
                        </div>
                        <div className="text-sm text-secondary-500">
                          {experience} years • {season.display} pts · {season.label}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleDropPlayer(player)}
                        className="btn-outline-secondary btn-sm text-danger-600 border-danger-300 hover:bg-danger-50"
                      >
                        Drop
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {getAllRosterPlayers().length === 0 && (
              <div className="text-center py-12">
                <TrophyIcon className="w-16 h-16 text-secondary-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-secondary-900 mb-2">No players yet</h3>
                <p className="text-secondary-600 mb-6">
                  Add real NFL kickers and punters to build your roster.
                </p>
                <button
                  onClick={() => waiversEnabled ? navigate(`/app/leagues/${leagueId}/waivers`) : setShowAddPlayer(true)}
                  className="btn-primary"
                >
                  Add Your First Player
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats Tab */}
      {activeTab === 'stats' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="card">
              <div className="card-body text-center">
                <ChartBarIcon className="w-8 h-8 text-primary-600 mx-auto mb-2" />
                <div className="stat-value">{Number(pointsFor).toFixed(1)}</div>
                <div className="stat-label">Total Points</div>
              </div>
            </div>
            <div className="card">
              <div className="card-body text-center">
                <TrophyIcon className="w-8 h-8 text-success-600 mx-auto mb-2" />
                <div className="stat-value">{wins}</div>
                <div className="stat-label">Wins</div>
              </div>
            </div>
            <div className="card">
              <div className="card-body text-center">
                <span className="text-2xl mx-auto mb-2 block">🏈</span>
                <div className="stat-value">{getAllRosterPlayers().length}</div>
                <div className="stat-label">Roster Size</div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="font-display uppercase text-lg tracking-wide text-ink-900">Weekly Performance</h3>
            </div>
            <div className="card-body space-y-6">
              {matchupsLoading ? (
                <div className="flex items-center text-secondary-600">
                  <div className="loading-spinner w-4 h-4 mr-2" />
                  <span>Loading weekly performance...</span>
                </div>
              ) : matchupsError ? (
                <p className="text-secondary-600">Couldn't load weekly performance.</p>
              ) : myTrend.length === 0 ? (
                <p className="text-secondary-600">No completed weeks yet — the ledger opens after Week 1.</p>
              ) : (
                <>
                  <div>
                    <h4 className="font-display uppercase text-sm tracking-wide text-ink-900 mb-2">Points by Week</h4>
                    <LineChart
                      series={[
                        { label: 'Median', points: roundedMedianSeries, color: '#A8A49B', dashed: true },
                        { label: team?.team_name || 'You', points: myTrend, color: '#1b1a17', emphasis: true },
                      ]}
                      xTicks={weeks}
                      ariaLabel={`Points by week for ${team?.team_name}, versus league median`}
                    />
                  </div>
                  {weeklySplits && weeklySplits.length > 0 && (
                    <div>
                      <h4 className="font-display uppercase text-sm tracking-wide text-ink-900 mb-2">Production by Unit</h4>
                      <StackedBars
                        bars={weeklySplits.map(s => ({
                          x: s.week,
                          segments: [
                            { value: s.k_points, color: '#F4B400', label: 'K' },
                            { value: s.p_points, color: '#059669', label: 'P' },
                          ],
                        }))}
                        ariaLabel="Kicker versus punter points per week"
                      />
                      <div className="mt-2 flex gap-4 text-xs text-secondary-500">
                        <span><span className="inline-block w-2.5 h-2.5 mr-1 align-middle bg-gold" />Kickers</span>
                        <span><span className="inline-block w-2.5 h-2.5 mr-1 align-middle bg-success-600" />Punters</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <AddPlayerModal
        isOpen={showAddPlayer}
        onClose={() => setShowAddPlayer(false)}
        onSwapPlayer={handleSwapPlayer}
        currentRoster={getAllRosterPlayers()}
        leagueId={team?.league_id}
        teamId={team?.id}
      />

      <DropPlayerModal
        isOpen={showDropPlayer}
        onClose={() => setShowDropPlayer(false)}
        onConfirm={confirmDropPlayer}
        player={playerToDrop}
        isStarter={playerToDrop ? isPlayerStarter(playerToDrop.id) : false}
      />
    </div>
  );
}